import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  ShoppingCart,
  Bike,
  TrendingUp,
  FileCheck2,
} from 'lucide-react';
import { StatCard } from '@components/dashboard/StatCard';
import { QuickActions, QuickAction } from '@components/dashboard/QuickActions';
import { RevenueChart } from '@components/dashboard/RevenueChart';
import { analyticsService } from '@services/analytics.service';
import { PermissionGate } from '@components/PermissionGate';
import { formatCurrency } from '@lib/utils';

export const DashboardPage = () => {
  const navigate = useNavigate();

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => analyticsService.getDashboard(),
    // If the endpoint isn't built yet, fail silently
    retry: false,
  });

  const quickActions: QuickAction[] = [
    {
      id: '1',
      title: 'Review KYC',
      count: `${stats?.pendingKYC ?? 0} pending`,
      icon: FileCheck2,
      variant: 'purple',
      onClick: () => navigate('/kyc'),
    },
    {
      id: '2',
      title: 'View Orders',
      count: `${stats?.activeOrders ?? 0} active`,
      icon: ShoppingCart,
      variant: 'orange',
      onClick: () => navigate('/orders'),
    },
    {
      id: '3',
      title: 'Manage Businesses',
      count: `${stats?.totalBusinesses ?? 0} registered`,
      icon: Building2,
      variant: 'green',
      onClick: () => navigate('/businesses'),
    },
  ];

  return (
    <div className="space-y-8">
      {/* Page heading */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2">
          Overview
        </p>
        <h2 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
          Platform at a glance
        </h2>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <PermissionGate permission="businesses.read">
          <StatCard
            label="Active Businesses"
            value={stats?.totalBusinesses?.toString() ?? '—'}
            icon={Building2}
            trend={undefined}
            footer="Total registered"
            variant="purple"
          />
        </PermissionGate>

        <PermissionGate permission="orders.read">
          <StatCard
            label="Active Orders"
            value={stats?.activeOrders?.toString() ?? '—'}
            icon={ShoppingCart}
            trend={stats?.orderGrowth ? { value: stats.orderGrowth, isPositive: stats.orderGrowth > 0 } : undefined}
            footer="Orders in progress"
            variant="orange"
          />
        </PermissionGate>

        <PermissionGate permission="reports.financial">
          <StatCard
            label="Revenue"
            value={stats?.totalRevenue != null ? formatCurrency(stats.totalRevenue) : '—'}
            icon={TrendingUp}
            trend={stats?.revenueGrowth ? { value: stats.revenueGrowth, isPositive: stats.revenueGrowth > 0 } : undefined}
            footer="This period"
            variant="green"
          />
        </PermissionGate>

        <PermissionGate permission="riders.read">
          <StatCard
            label="Online Riders"
            value={stats?.onlineRiders?.toString() ?? '—'}
            icon={Bike}
            variant="red"
            footer="Currently active"
          />
        </PermissionGate>
      </div>

      {/* Quick Actions + Revenue Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RevenueChart />
        </div>
        <div className="lg:col-span-1">
          <QuickActions actions={quickActions} />
        </div>
      </div>
    </div>
  );
};
